import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Save, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useToast } from '@/shared/components/Toast';
import {
  useAdvisorAcceleratorLink,
  useSaveAdvisorAcceleratorLink,
  useDeleteAdvisorAcceleratorLink,
} from '../hooks/useFpResources';

const URL_REGEX = /^https?:\/\/.+/;
const MAX_URL = 2048;
const MAX_LABEL = 120;
const MAX_DESCRIPTION = 500;

type ApiErrorShape = {
  message?: string;
  response?: { data?: { message?: string | string[] } };
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = error as ApiErrorShape;
  const serverMessage = apiError.response?.data?.message;
  if (Array.isArray(serverMessage) && serverMessage.length > 0) {
    return serverMessage.join('; ');
  }
  if (typeof serverMessage === 'string' && serverMessage.length > 0) {
    return serverMessage;
  }
  if (typeof apiError.message === 'string' && apiError.message.length > 0) {
    return apiError.message;
  }
  return fallback;
};

export const AdvisorAcceleratorLinkForm = () => {
  const { showToast } = useToast();
  const { data, isLoading } = useAdvisorAcceleratorLink();
  const save = useSaveAdvisorAcceleratorLink();
  const remove = useDeleteAdvisorAcceleratorLink();

  const [url, setUrl] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [urlError, setUrlError] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (data === undefined) return;
    setUrl(data?.url ?? '');
    setButtonLabel(data?.buttonLabel ?? '');
    setDescription(data?.description ?? '');
    setEnabled(data?.enabled ?? true);
  }, [data]);

  useEffect(() => {
    if (confirmClear) {
      confirmButtonRef.current?.focus();
    }
  }, [confirmClear]);

  useEffect(() => {
    if (!confirmClear) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmClear(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmClear]);

  const handleSave = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!URL_REGEX.test(url)) {
      setUrlError('Must be a valid http or https URL');
      return;
    }
    setUrlError('');
    try {
      await save.mutateAsync({
        url: url.trim(),
        buttonLabel: buttonLabel.trim() || undefined,
        description: description.trim() || undefined,
        enabled,
      });
      showToast('Advisor Accelerator link saved.', 'success');
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to save Advisor Accelerator link.'), 'error');
    }
  };

  const handleClear = async () => {
    try {
      await remove.mutateAsync();
      setUrl('');
      setButtonLabel('');
      setDescription('');
      setEnabled(true);
      setConfirmClear(false);
      showToast('Advisor Accelerator link cleared.', 'success');
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to clear Advisor Accelerator link.'), 'error');
    }
  };

  if (isLoading) {
    return (
      <div data-testid="advisor-accelerator-link-skeleton" className="space-y-4">
        <div className="space-y-1">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-24 w-full" />
        </div>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-32" />
      </div>
    );
  }

  return (
    <form className="space-y-4" onSubmit={handleSave}>
      <div className="space-y-1">
        <Label htmlFor="aa-url">URL</Label>
        <Input
          id="aa-url"
          type="url"
          inputMode="url"
          maxLength={MAX_URL}
          value={url}
          placeholder="https://marketing.thebquest.com/advisor-accelerator"
          aria-invalid={!!urlError}
          aria-describedby={urlError ? 'aa-url-error' : undefined}
          onChange={(e) => { setUrl(e.target.value); setUrlError(''); }}
        />
        {urlError && (
          <p id="aa-url-error" className="text-sm text-red-600">{urlError}</p>
        )}
      </div>

      <div className="space-y-1">
        <Label htmlFor="aa-label">Button Label</Label>
        <Input
          id="aa-label"
          maxLength={MAX_LABEL}
          value={buttonLabel}
          placeholder="View Advisor Accelerator"
          onChange={(e) => setButtonLabel(e.target.value)}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="aa-description">Description</Label>
        <Textarea
          id="aa-description"
          maxLength={MAX_DESCRIPTION}
          value={description}
          placeholder="Short helper text shown on the FP card"
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
        <Label htmlFor="aa-enabled">Enabled</Label>
        <Switch id="aa-enabled" checked={enabled} onCheckedChange={setEnabled} />
      </div>

      <div className="flex items-center justify-between">
        {data && !confirmClear && (
          <Button type="button" variant="outline" onClick={() => setConfirmClear(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Clear
          </Button>
        )}
        {confirmClear && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Remove this link for all FPs?</span>
            <Button
              ref={confirmButtonRef}
              type="button"
              variant="destructive"
              onClick={handleClear}
              disabled={remove.isPending}
            >
              Confirm
            </Button>
            <Button type="button" variant="outline" onClick={() => setConfirmClear(false)}>
              Cancel
            </Button>
          </div>
        )}
        <Button
          type="submit"
          className="ml-auto"
          disabled={save.isPending}
        >
          <Save className="mr-2 h-4 w-4" />
          {save.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
};
